/* eslint-disable max-lines */
import dayjs from "dayjs";
import { FC } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { ICreditActivityDay } from "../../../interfaces/wallet/Wallet.types";
import { eur } from "../../../utils/eur";
import Box from "../../ui/Box";
import SpinnerLoader from "../../ui/SpinnerLoader";

export const ACTIVITY_SERIES: {
  key: keyof Omit<ICreditActivityDay, "date">;
  label: string;
  color: string;
}[] = [
  { key: "issued", label: "Të lëshuara", color: "#3A81F6" },
  { key: "redeemed", label: "Të shpenzuara", color: "#36B37E" },
  { key: "expired", label: "Të skaduara", color: "#F15B2D" },
  { key: "returned", label: "Të kthyera", color: "#a855f7" }
];

// Sized for the top tick, not the largest value: recharts rounds the axis up (60 -> 100,
// 950 -> 1000), so it gets one digit of headroom, and at least room for "0.25"/"100" plus "€".
export const yAxisWidth = (data?: ICreditActivityDay[]): number => {
  const max = Math.max(0, ...(data ?? []).flatMap((d) => ACTIVITY_SERIES.map((s) => d[s.key])));
  const labelLength = Math.max(Math.ceil(max).toFixed(0).length + 1, 4) + 1;
  return (labelLength + 1) * 8;
};

const CreditActivityGraph: FC<{
  data?: ICreditActivityDay[];
  loading?: boolean;
}> = ({ data, loading }) => {

  return (
    <Box shadow="shadow-xl" padding="px-4 sm:px-6 pt-6 pb-3.5" className="w-full">
      <div className="relative w-full min-h-[320px]">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data || []}>
            <defs>
              {ACTIVITY_SERIES.map((s) => (
                <linearGradient
                  key={s.key}
                  id={`credit-${s.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            {data !== undefined && !loading && (
              <XAxis
                dataKey="date"
                axisLine={false}
                tickSize={0}
                tickFormatter={(date) => dayjs(date).format("DD/MM/YYYY")}
                interval="preserveStartEnd"
                fontSize={12}
                dy={16}
                color="#6E6E6E"
              />
            )}
            {data !== undefined && !loading && (
              <YAxis
                axisLine={false}
                tickLine={false}
                fontSize={12}
                color="#6E6E6E"
                tickFormatter={(v: number) => `${v}€`}
                width={yAxisWidth(data)}
              />
            )}
            <CartesianGrid
              strokeDasharray="4"
              stroke="#EAEAEA"
              vertical={false}
            />
            <Tooltip
              cursor={{
                stroke: "#2C2C2C",
                fill: "#2C2C2C",
                opacity: 0.5,
                strokeWidth: 1
              }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const day = payload[0].payload as ICreditActivityDay;

                return (
                  <Box
                    padding="px-4 py-3"
                    className="flex flex-col items-start text-sm leading-4 text-black
                    outline-none min-w-[170px]"
                  >
                    {ACTIVITY_SERIES.map((s) => (
                      <span key={s.key} className="mb-1.5 flex items-center text-neutral">
                        <span
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: s.color }}
                        />
                        <span className="font-bold text-black mr-1">
                          {eur(day[s.key])}
                        </span>
                        {s.label.toLowerCase()}
                      </span>
                    ))}
                    <span className="mt-0.5 text-neutral">
                      {dayjs(day.date).format("DD/MM/YYYY")}
                    </span>
                  </Box>
                );
              }}
              wrapperStyle={{ outline: "none" }}
            />
            {data !== undefined &&
              !loading &&
              ACTIVITY_SERIES.map((s) => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={`url(#credit-${s.key})`}
                  dot={data.length === 1}
                />
              ))}
            <Legend
              verticalAlign="bottom"
              content={() => (
                <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 mt-5">
                  {ACTIVITY_SERIES.map((s) => (
                    <div key={s.key} className="flex items-center">
                      <div
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: s.color }}
                      />
                      <p className="text-sm font-medium leading-[18px]">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            />
          </AreaChart>
        </ResponsiveContainer>
        {loading ? (
          <SpinnerLoader className="absolute top-0 w-full h-full flex justify-center items-center" />
        ) : null}
      </div>
    </Box>
  );
};

export default CreditActivityGraph;
